import React, { useEffect } from "react";
import {
  Box,
  Paper,
  Select,
  MenuItem,
  Button,
  TextField,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  RadioGroup,
  Radio,
  FormLabel,
  FormGroup,
  Autocomplete,
  Switch,
  Tooltip,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import KeywordBuilder from "./KeywordBuilder";
import SubtestRangeRecommendButton from "./SubtestRangeRecommendButton";
import KeywordRecommendButton from "./KeywordRecommendButton";
import {
  fetchCategorySubtests,
  setOption,
  updateSubtest,
  deleteSubtest,
  splitSubtest,
  saveNormalRuleset,
} from "../reducers/ruleFrameSlice";
import BloodTestsManager from "./BloodTestManager";
import { DataGrid } from "@mui/x-data-grid";

function BloodTestsTable({ subTests, deploymentType, license_key }) {
  const dispatch = useDispatch();
  const { selectedCategory } = useSelector((state) => state.ruleFrame);

  const handleDelete = (id) => {
    dispatch(deleteSubtest(id));
  };

  const handleSplit = (id) => {
    dispatch(splitSubtest(id));
  };

  const handleEdit = (id, currentName) => {
    const newName = prompt("Enter new subtest name:", currentName);
    if (newName && newName.trim()) {
      dispatch(updateSubtest({ id, changes: { sub_test_name: newName } }));
    }
  };

  const handleFieldChange = (id, field, value) => {
    dispatch(updateSubtest({ id, changes: { [field]: value } }));
  };

  const handleApplySuggestions = (baseRow, selectedSuggestions) => {
    if (!selectedSuggestions?.length) return;

    const first = selectedSuggestions[0];

    dispatch(
      updateSubtest({
        id: baseRow.id,
        changes: {
          gender: first.gender || "",
          min_age: first.min_age,
          max_age: first.max_age,
          min_val: first.min_val,
          max_val: first.max_val,
        },
      }),
    );

    selectedSuggestions.slice(1).forEach((row) => {
      dispatch({
        type: "ruleFrame/addSubtest",
        payload: {
          sub_test_name: baseRow.sub_test_name,
          gender: row.gender || "",
          min_age: row.min_age,
          max_age: row.max_age,
          min_val: row.min_val,
          max_val: row.max_val,
        },
      });
    });
  };

  const columns = [
    {
      field: "sub_test_name",
      headerName: "Test Name",
      renderHeader: () => (
        <strong>Pathology Tests – {selectedCategory || "None"}</strong>
      ),
      flex: 2.5,
      minWidth: 120,
      renderCell: (params) => {
        const { sub_test_name, gender, min_age, max_age } = params.row;

        // Build gender label
        let genderLabel = "";
        if (gender === "Male") genderLabel = "M";
        else if (gender === "Female") genderLabel = "F";

        // Build age label
        let ageLabel = "";
        if (min_age && max_age) {
          ageLabel = `${min_age} to ${max_age}`;
        } else if (min_age) {
          ageLabel = `${min_age}+`;
        } else if (max_age) {
          ageLabel = `0 to ${max_age}`;
        }

        // Combine parts
        let suffix = "";
        if (genderLabel || ageLabel) {
          if (genderLabel && ageLabel) {
            suffix = ` (${genderLabel} - ${ageLabel})`;
          } else if (genderLabel) {
            suffix = ` (${genderLabel})`;
          } else if (ageLabel) {
            suffix = ` (${ageLabel})`;
          }
        }

        return `${sub_test_name}${suffix}`;
      },
    },

    // --- New Gender Column ---
    {
      field: "gender",
      headerName: "Gender",
      flex: 0.6,
      renderCell: (params) => (
        <Select
          defaultValue={params.row.gender ?? ""}
          onChange={(e) =>
            handleFieldChange(params.row.id, "gender", e.target.value)
          }
          sx={{
            width: "100%",
            "& .MuiSelect-select": {
              fontSize: "0.75rem",
              padding: "2px 6px",
              height: "20px",
              display: "flex",
              alignItems: "center",
            },
          }}
        >
          <MenuItem value="" sx={{ fontSize: "0.75rem", height: "26px" }}>
            None
          </MenuItem>
          <MenuItem value="Male" sx={{ fontSize: "0.75rem", height: "26px" }}>
            Male
          </MenuItem>
          <MenuItem value="Female" sx={{ fontSize: "0.75rem", height: "26px" }}>
            Female
          </MenuItem>
        </Select>
      ),
    },
    {
      field: "min_age",
      headerName: "Min Age",
      flex: 0.6,
      renderCell: (params) => (
        <TextField
          defaultValue={params.row.min_age ?? ""}
          type="number"
          onBlur={(e) =>
            handleFieldChange(params.row.id, "min_age", e.target.value)
          }
          sx={{
            width: "100%",
            "& .MuiInputBase-input": {
              fontSize: "0.75rem",
              padding: "2px 6px",
              height: "20px",
            },
          }}
        />
      ),
    },
    {
      field: "max_age",
      headerName: "Max Age",
      flex: 0.6,
      renderCell: (params) => (
        <TextField
          defaultValue={params.row.max_age ?? ""}
          type="number"
          onBlur={(e) =>
            handleFieldChange(params.row.id, "max_age", e.target.value)
          }
          sx={{
            width: "100%",
            "& .MuiInputBase-input": {
              fontSize: "0.75rem",
              padding: "2px 6px",
              height: "20px",
            },
          }}
        />
      ),
    },

    { field: "lab_min_val", headerName: "Lab Min", flex: 0.5 },
    { field: "lab_max_val", headerName: "Lab Max", flex: 0.5 },

    {
      field: "min_val",
      headerName: "Clin Min",
      flex: 0.6,
      renderCell: (params) => (
        <TextField
          defaultValue={params.row.min_val ?? ""}
          type="number"
          onBlur={(e) =>
            handleFieldChange(params.row.id, "min_val", e.target.value)
          }
          sx={{
            width: "100%",
            "& .MuiInputBase-input": {
              fontSize: "0.75rem",
              padding: "2px 6px",
              height: "20px",
            },
          }}
        />
      ),
    },
    {
      field: "max_val",
      headerName: "Clin Max",
      flex: 0.6,
      renderCell: (params) => (
        <TextField
          defaultValue={params.row.max_val ?? ""}
          type="number"
          onBlur={(e) =>
            handleFieldChange(params.row.id, "max_val", e.target.value)
          }
          sx={{
            width: "100%",
            "& .MuiInputBase-input": {
              fontSize: "0.75rem",
              padding: "2px 6px",
              height: "20px",
            },
          }}
        />
      ),
    },
    {
      field: "previous_type",
      headerName: "Prev Result",
      flex: 1,
      renderCell: (params) => (
        <Select
          defaultValue={params.row.previous_type ?? 0}
          onChange={(e) =>
            handleFieldChange(
              params.row.id,
              "previous_type",
              parseInt(e.target.value, 10),
            )
          }
          sx={{
            width: "100%",
            "& .MuiSelect-select": {
              fontSize: "0.75rem",
              padding: "2px 6px",
              height: "20px",
              display: "flex",
              alignItems: "center",
            },
          }}
        >
          <MenuItem
            value={0}
            sx={{ width: "100%", fontSize: "0.75rem", height: "26px" }}
          >
            No Check
          </MenuItem>
          <MenuItem
            value={1}
            sx={{ width: "100%", fontSize: "0.75rem", height: "26px" }}
          >
            Prev test within X days
          </MenuItem>
          <MenuItem
            value={2}
            sx={{ width: "100%", fontSize: "0.75rem", height: "26px" }}
          >
            Prev result
          </MenuItem>
        </Select>
      ),
    },
    {
      field: "days_or_tests",
      headerName: "Days",
      flex: 0.6,
      renderCell: (params) => {
        const isDisabled =
          params.row.previous_type === 0 || params.row.previous_type === 2;

        return (
          <TextField
            defaultValue={params.row.days_or_tests ?? ""}
            onBlur={(e) =>
              handleFieldChange(params.row.id, "days_or_tests", e.target.value)
            }
            onChange={(e) => {
              if (isDisabled) {
                handleFieldChange(params.row.id, "days_or_tests", null);
              }
            }}
            type="number"
            disabled={isDisabled}
            sx={{
              width: "100%",
              "& .MuiInputBase-input": {
                fontSize: "0.75rem",
                padding: "2px 6px",
                height: "20px",
              },
            }}
          />
        );
      },
    },
    {
      field: "pdiv_below",
      headerName: "% Below",
      flex: 0.6,
      renderCell: (params) => {
        const isDisabled = params.row.previous_type === 0;

        return (
          <TextField
            defaultValue={params.row.pdiv_below ?? ""}
            type="number"
            onBlur={(e) =>
              handleFieldChange(params.row.id, "pdiv_below", e.target.value)
            }
            onChange={(e) => {
              if (isDisabled) {
                handleFieldChange(params.row.id, "pdiv_below", null);
              }
            }}
            disabled={isDisabled}
            sx={{
              width: "100%",
              "& .MuiInputBase-input": {
                fontSize: "0.75rem",
                padding: "2px 6px",
                height: "20px",
              },
            }}
          />
        );
      },
    },
    {
      field: "pdiv_above",
      headerName: "% Above",
      flex: 0.6,
      renderCell: (params) => {
        const isDisabled = params.row.previous_type === 0;

        return (
          <TextField
            defaultValue={params.row.pdiv_above ?? ""}
            type="number"
            onBlur={(e) =>
              handleFieldChange(params.row.id, "pdiv_above", e.target.value)
            }
            onChange={(e) => {
              if (isDisabled) {
                handleFieldChange(params.row.id, "pdiv_above", null);
              }
            }}
            disabled={isDisabled}
            sx={{
              width: "100%",
              "& .MuiInputBase-input": {
                fontSize: "0.75rem",
                padding: "2px 6px",
                height: "20px",
              },
            }}
          />
        );
      },
    },

    ...(deploymentType !== "runner"
      ? [
          {
            field: "suggest_ranges",
            headerName: "",
            flex: 0.2,
            sortable: false,
            renderCell: (params) => (
              <SubtestRangeRecommendButton
                licenseKey={license_key}
                category={selectedCategory}
                subtest={params.row}
                onApply={(selectedRows) =>
                  handleApplySuggestions(params.row, selectedRows)
                }
              />
            ),
          },
          {
            field: "split",
            headerName: "Split",
            flex: 0.5,
            renderCell: (params) => (
              <Button
                color="primary"
                variant="dense"
                onClick={() => {
                  const bloodTestName = params.row.sub_test_name;
                  const result = window.confirm(
                    `Are you sure you want split ${bloodTestName} for both genders?`,
                  );
                  if (result) {
                    handleSplit(params.row.id);
                  } else {
                    window.alert("The action has been cancelled.");
                  }
                }}
              >
                Split
              </Button>
            ),
          },
          {
            field: "delete",
            headerName: "Delete",
            flex: 0.5,
            renderCell: (params) => (
              <Button
                color="error"
                variant="dense"
                onClick={() => {
                  const bloodTestName = params.row.sub_test_name;
                  const result = window.confirm(
                    `Are you sure you want to delete ${bloodTestName}?`,
                  );
                  if (result) {
                    handleDelete(params.row.id);
                  } else {
                    window.alert("The action has been cancelled.");
                  }
                }}
              >
                Delete
              </Button>
            ),
          },
          {
            field: "edit",
            headerName: "Edit",
            flex: 0.5,
            renderCell: (params) => (
              <Button
                color="primary"
                variant="dense"
                onClick={() =>
                  handleEdit(params.row.id, params.row.sub_test_name)
                }
              >
                Edit
              </Button>
            ),
          },
          {
            field: "deactivated",
            headerName: "Active",
            flex: 0.6,
            renderCell: (params) => (
              <Tooltip
                title={params.row.deactivated ? "Deactivated" : "Active"}
              >
                <Switch
                  size="small"
                  checked={!params.row.deactivated}
                  onChange={(e) =>
                    handleFieldChange(
                      params.row.id,
                      "deactivated",
                      !e.target.checked,
                    )
                  }
                  color="success"
                />
              </Tooltip>
            ),
          },
        ]
      : []),
  ];

  return (
    <div style={{ width: "100%" }}>
      <DataGrid
        rows={subTests}
        columns={columns}
        variant="superdense"
        density="compact"
        sx={{
          height: 470,
          "& .MuiDataGrid-columnHeaders": {
            marginBottom: "0px", // space between header and first row
          },
        }}
        disableColumnMenu
        pageSize={20}
        rowsPerPageOptions={[20]}
        disableSelectionOnClick
        getRowId={(row) => row.id}
        initialState={{
          sorting: {
            sortModel: [{ field: "sub_test_name", sort: "asc" }],
          },
        }}
      />
    </div>
  );
}

const NormalRuleFrame = () => {
  const dispatch = useDispatch();
  const {
    selectedCategory,
    ruleloading,
    ruleerror,
    rulesuccess,
    subtests,
    options,
    deploymentType,
    category_data,
  } = useSelector((state) => state.ruleFrame);

  const license_key = useSelector(
    (state) => state.authen.loggedInUser?.license_key,
  );

  const defaultComments = [
    "Normal No Action",
    "** Normal",
    "** Abnormal",
    "** Satisfactory",
    "*Expected/stable",
    "*On correct treatment",
  ];

  const validateForm = () => {
    let errors = [];

    for (const st of subtests) {
      const min =
        st.min_val !== null && st.min_val !== ""
          ? parseFloat(st.min_val)
          : null;
      const max =
        st.max_val !== null && st.max_val !== ""
          ? parseFloat(st.max_val)
          : null;

      if (min === null || max === null) {
        errors.push(
          `Min and Max value must be specified for ${st.sub_test_name}`,
        );
      }

      if (min !== null && max !== null) {
        if (!(min === 0 && max === 0) && min >= max) {
          errors.push(`Max must be greater than Min for ${st.sub_test_name}`);
        }
      }

      // Ensure clinician ranges match lab ranges when followLab is enabled
      if (options.followLab) {
        const labMin =
          st.lab_min_val !== null && st.lab_min_val !== ""
            ? parseFloat(st.lab_min_val)
            : null;

        const labMax =
          st.lab_max_val !== null && st.lab_max_val !== ""
            ? parseFloat(st.lab_max_val)
            : null;

        if (labMin !== null && min !== null && min !== labMin) {
          errors.push(
            `Clinician Min Val must match Lab Min Val for ${st.sub_test_name}. Disable Automatically follow and file lab min max to use different values.`,
          );
        }

        if (labMax !== null && max !== null && max !== labMax) {
          errors.push(
            `Clinician Max Val must match Lab Max Val for ${st.sub_test_name}. Disable Automatically follow and file lab min max to use different values.`,
          );
        }
      }

      const minAge =
        st.min_age !== null && st.min_age !== "" ? parseInt(st.min_age) : null;
      const maxAge =
        st.max_age !== null && st.max_age !== "" ? parseInt(st.max_age) : null;

      if (minAge !== null && String(minAge) !== st.min_age) {
        errors.push(
          `Min Age must be a positive whole number for ${st.sub_test_name}`,
        );
      }
      if (maxAge !== null && String(maxAge) !== st.max_age) {
        errors.push(
          `Max Age must be a positive whole number for ${st.sub_test_name}`,
        );
      }

      if (minAge !== null) {
        if (!Number.isInteger(minAge) || minAge < 0) {
          errors.push(
            `Min Age must be a positive whole number for ${st.sub_test_name}`,
          );
        }
      }

      if (maxAge !== null) {
        if (!Number.isInteger(maxAge) || maxAge < 0) {
          errors.push(
            `Max Age must be a positive whole number for ${st.sub_test_name}`,
          );
        }
      }

      if (
        (minAge !== null && maxAge === null) ||
        (minAge === null && maxAge !== null)
      ) {
        errors.push(
          `Both Min Age and Max Age must be specified for ${st.sub_test_name}`,
        );
      }

      if (minAge !== null && maxAge !== null) {
        if (minAge > maxAge) {
          errors.push(
            `Max Age must be greater than Min Age for ${st.sub_test_name}`,
          );
        }
      }

      if (st.previous_type === 1) {
        if (!st.days_or_tests || (!st.pdiv_below && !st.pdiv_above)) {
          errors.push(
            `Previous test within X days missing supporting fields for ${st.sub_test_name}`,
          );
        }
      }

      if (st.previous_type === 2) {
        if (!st.pdiv_below && !st.pdiv_above) {
          errors.push(
            `Previous result missing supporting fields for ${st.sub_test_name}`,
          );
        }
      }
    }

    if (
      options.keywordRule > 0 &&
      (typeof options.keywords !== "string" || options.keywords.trim() === "")
    ) {
      errors.push("Keywords must be specified if Keyword Rule is active");
    }

    const seen = new Set();

    for (const st of subtests) {
      const key = [
        st.sub_test_name?.toLowerCase(),
        st.gender || "",
        st.min_age || "",
        st.max_age || "",
      ].join("|");

      if (seen.has(key)) {
        errors.push(
          `Duplicate test detected: ${st.sub_test_name} (${st.gender || "Any"})`,
        );
      } else {
        seen.add(key);
      }
    }

    return errors;
  };
  const handleSave = () => {
    dispatch(
      saveNormalRuleset({
        subtests,
        options,
        license_key,
        category: selectedCategory,
      }),
    );
  };

  useEffect(() => {
    if (options.fileMethod === 1 && !options.fileComment) {
      dispatch(
        setOption({
          key: "fileComment",
          value: "Normal No Action",
        }),
      );
    }

    if (options.fileMethod === 0) {
      dispatch(
        setOption({
          key: "fileComment",
          value: "",
        }),
      );
    }
  }, [options.fileMethod, options.fileComment, dispatch]);

  useEffect(() => {
    if (selectedCategory && license_key) {
      dispatch(
        fetchCategorySubtests({ categoryName: selectedCategory, license_key }),
      );
    }
  }, [dispatch, license_key, selectedCategory]);

  // Sync category_data → options
  useEffect(() => {
    if (category_data) {
      dispatch(
        setOption({
          key: "skipWithoutRanges",
          value: category_data.skip_without_ranges,
        }),
      );
      dispatch(
        setOption({ key: "fileNormal", value: category_data.file_normal }),
      );
      dispatch(
        setOption({ key: "followLab", value: category_data.follow_lab }),
      );
      dispatch(
        setOption({ key: "keywords", value: category_data.keyword || "" }),
      );
      dispatch(
        setOption({
          key: "fileComment",
          value: category_data.file_comment || "",
        }),
      );

      dispatch(
        setOption({
          key: "keywordRule",
          value: category_data.keyword_check || 0,
        }),
      );
      dispatch(
        setOption({ key: "fileMethod", value: category_data.file_method || 0 }),
      );
    }
  }, [category_data, dispatch]);

  useEffect(() => {
    subtests.forEach((st) => {
      if (st.previous_type === 0) {
        if (
          st.days_or_tests !== null ||
          st.pdiv_below !== null ||
          st.pdiv_above !== null
        ) {
          dispatch(
            updateSubtest({
              id: st.id,
              changes: {
                days_or_tests: null,
                pdiv_below: null,
                pdiv_above: null,
              },
            }),
          );
        }
      } else if (st.previous_type === 2) {
        if (st.days_or_tests !== null) {
          dispatch(
            updateSubtest({
              id: st.id,
              changes: { days_or_tests: null },
            }),
          );
        }
      }
    });
  }, [subtests, dispatch]);

  const errors = validateForm();
  const isValid = errors.length === 0;

  return (
    <>
      {/* Second Paper */}
      <Paper
        elevation={2}
        sx={{
          p: 1.5,
          display: "flex",
          flexWrap: "wrap",
          gap: 2,
          alignItems: "flex-start",
          justifyContent: "space-between",
        }}
      >
        {/* Section for Skip/File checkboxes */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            minWidth: 180,
            flex: 1,
          }}
        >
          <FormLabel sx={{ fontSize: "0.8rem", mb: 0.5 }}>
            Filing Options
          </FormLabel>
          <FormGroup sx={{ m: 0 }}>
            <FormControlLabel
              sx={{ m: 0 }}
              control={
                <Checkbox
                  size="small"
                  sx={{ p: 0 }}
                  checked={options.skipWithoutRanges}
                  onChange={(e) => {
                    const newValue = e.target.checked;
                    const messageOn =
                      'When selecting - Please note by selecting the "Skip if no lab ranges are present" option, ' +
                      "MyBotGP will not file the test if no subtest with ranges are found.";
                    const messageOff =
                      'When de-selecting - Please note by de-selecting the "Skip if no lab ranges are present" option, ' +
                      "MyBotGP will file the test if category check pass and no subtest ranges are found.";
                    const result = window.confirm(
                      newValue ? messageOn : messageOff,
                    );
                    if (result) {
                      dispatch(
                        setOption({
                          key: "skipWithoutRanges",
                          value: newValue,
                        }),
                      );
                    } else {
                      e.preventDefault();
                      window.alert("The action has been cancelled.");
                    }
                  }}
                />
              }
              label={
                <span style={{ fontSize: "0.8rem" }}>
                  Skip if no lab ranges are present
                </span>
              }
            />
            <FormControlLabel
              sx={{ m: 0 }}
              control={
                <Checkbox
                  size="small"
                  sx={{ p: 0 }}
                  checked={options.fileNormal}
                  onChange={(e) => {
                    const newValue = e.target.checked;
                    const messageOn =
                      'When selecting - Please note by selecting the "File for Normal" option, ' +
                      "MyBotGP will file based on the Lab classifying the test as Normal - all rules set " +
                      "within MyBotGP will be bypassed and not applied.";
                    const messageOff =
                      'When de-selecting - Please note by de-selecting the "File for Normal" option, ' +
                      "MyBotGP will file based on the rules set within MyBotGP and not where the Lab has " +
                      "classified the test as Normal.";
                    const result = window.confirm(
                      newValue ? messageOn : messageOff,
                    );

                    if (result) {
                      dispatch(
                        setOption({ key: "fileNormal", value: newValue }),
                      );
                    } else {
                      e.preventDefault();
                      window.alert("The action has been cancelled.");
                    }
                  }}
                />
              }
              label={
                <span style={{ fontSize: "0.8rem" }}>
                  Follow the lab file normal
                </span>
              }
            />
            <FormControlLabel
              sx={{ m: 0 }}
              control={
                <Checkbox
                  size="small"
                  sx={{ p: 0 }}
                  checked={options.followLab}
                  onChange={(e) => {
                    const newValue = e.target.checked;
                    const messageOn =
                      'When selecting - Please note by selecting the "Automatically follow and file" option, ' +
                      "MyBotGP will updated existing subtest ranges with lab min and max values. The category" +
                      " will continue to be enabled.";
                    const messageOff =
                      'When de-selecting - Please note by de-selecting the "Automatically follow and file" option, ' +
                      "MyBotGP will update existing subtest lab ranges and remove the catebory to In Review.";
                    const result = window.confirm(
                      newValue ? messageOn : messageOff,
                    );
                    if (newValue) {
                      const invalidSubtest = subtests.find((st) => {
                        const min =
                          st.min_val !== null && st.min_val !== ""
                            ? parseFloat(st.min_val)
                            : null;

                        const max =
                          st.max_val !== null && st.max_val !== ""
                            ? parseFloat(st.max_val)
                            : null;

                        const labMin =
                          st.lab_min_val !== null && st.lab_min_val !== ""
                            ? parseFloat(st.lab_min_val)
                            : null;

                        const labMax =
                          st.lab_max_val !== null && st.lab_max_val !== ""
                            ? parseFloat(st.lab_max_val)
                            : null;

                        return (
                          (labMin !== null && min !== null && min !== labMin) ||
                          (labMax !== null && max !== null && max !== labMax)
                        );
                      });

                      if (invalidSubtest) {
                        window.alert(
                          `Clinician ranges must match lab ranges before enabling Automatically follow and file lab min max.\n\nFailed test: ${invalidSubtest.sub_test_name}`,
                        );
                        return;
                      }
                    }

                    if (result) {
                      dispatch(
                        setOption({ key: "followLab", value: newValue }),
                      );
                    } else {
                      e.preventDefault();
                      window.alert("The action has been cancelled.");
                    }
                  }}
                />
              }
              label={
                <span style={{ fontSize: "0.8rem" }}>
                  Automatically follow and file lab min max
                </span>
              }
            />
          </FormGroup>
        </Box>

        {/* Keyword Rule */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            minWidth: 180,
            flex: 1,
          }}
        >
          <FormLabel sx={{ fontSize: "0.8rem", mb: 0.5 }}>
            Apply Keyword Rule
          </FormLabel>
          <RadioGroup
            value={options.keywordRule}
            onChange={(e) =>
              dispatch(
                setOption({
                  key: "keywordRule",
                  value: parseInt(e.target.value, 10),
                }),
              )
            }
          >
            <FormControlLabel
              value={0}
              control={<Radio size="small" sx={{ p: 0 }} />}
              label={
                <span style={{ fontSize: "0.8rem" }}>No Keyword Check</span>
              }
            />
            <FormControlLabel
              value={1}
              control={<Radio size="small" sx={{ p: 0 }} />}
              label={
                <span style={{ fontSize: "0.8rem" }}>Keyword is Present</span>
              }
            />
            <FormControlLabel
              value={2}
              control={<Radio size="small" sx={{ p: 0 }} />}
              label={
                <span style={{ fontSize: "0.8rem" }}>Keyword is Absent</span>
              }
            />
            <FormControlLabel
              value={3}
              control={<Radio size="small" sx={{ p: 0 }} />}
              label={
                <span style={{ fontSize: "0.8rem" }}>
                  Keyword Absent in Problem Page
                </span>
              }
            />
          </RadioGroup>
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            minWidth: 220,
            mr: "30px",
            flex: 1,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 0.5,
            }}
          >
            <KeywordRecommendButton
              licenseKey={license_key}
              category={selectedCategory}
              mode="normal"
              disabled={options.keywordRule === 0}
              onApply={(rec) => {
                if (rec.keywords !== undefined)
                  dispatch(setOption({ key: "keywords", value: rec.keywords }));
                if (rec.keywordRule !== undefined)
                  dispatch(
                    setOption({ key: "keywordRule", value: rec.keywordRule }),
                  );
              }}
            />
          </Box>
          <KeywordBuilder
            value={options.keywords}
            onChange={(serialized) =>
              dispatch(setOption({ key: "keywords", value: serialized }))
            }
            disabled={options.keywordRule === 0}
          />
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            minWidth: 160,
            flex: 1,
          }}
        >
          <FormLabel sx={{ fontSize: "0.8rem", mb: 0.5 }}>
            Filing Approach
          </FormLabel>
          <RadioGroup
            value={options.fileMethod}
            onChange={(e) =>
              dispatch(
                setOption({
                  key: "fileMethod",
                  value: parseInt(e.target.value, 10),
                }),
              )
            }
          >
            <FormControlLabel
              value={0}
              control={<Radio size="small" sx={{ p: 0 }} />}
              label={<span style={{ fontSize: "0.8rem" }}>No Comments</span>}
            />
            <FormControlLabel
              value={1}
              control={<Radio size="small" sx={{ p: 0 }} />}
              label={<span style={{ fontSize: "0.8rem" }}>With Comments</span>}
            />
          </RadioGroup>

          <Autocomplete
            freeSolo
            options={defaultComments}
            value={options.fileComment || ""}
            disabled={options.fileMethod === 0}
            onChange={(e, newValue) =>
              dispatch(
                setOption({
                  key: "fileComment",
                  value: newValue,
                }),
              )
            }
            onInputChange={(e, newValue) =>
              dispatch(
                setOption({
                  key: "fileComment",
                  value: newValue,
                }),
              )
            }
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                placeholder="Select or type a comment"
                sx={{
                  "& .MuiInputBase-input": {
                    padding: "4px 6px",
                    fontSize: "0.8rem",
                  },
                  "& .MuiInputLabel-root": {
                    fontSize: "0.75rem",
                  },
                  "& .MuiOutlinedInput-root": {
                    height: 32,
                    marginTop: "8px",
                  },
                }}
              />
            )}
          />
        </Box>
      </Paper>
      {selectedCategory && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Paper elevation={2} sx={{ p: 2, maxHeight: 600 }}>
            <BloodTestsManager categoryId={selectedCategory.id} />
            <BloodTestsTable
              category={selectedCategory}
              subTests={subtests}
              deploymentType={deploymentType}
              license_key={license_key}
            />

            {errors.length > 0 && (
              <Box sx={{ color: "red", fontSize: "0.8rem", mb: 1 }}>
                {errors.map((err, idx) => (
                  <div key={idx}>{err}</div>
                ))}
              </Box>
            )}
            <Box sx={{ mt: 2, textAlign: "right" }}>
              {rulesuccess && (
                <Box sx={{ color: "green", fontSize: "0.8rem", mb: 1 }}>
                  {rulesuccess}
                </Box>
              )}

              {ruleerror && (
                <Box sx={{ color: "red", fontSize: "0.8rem", mb: 1 }}>
                  {ruleerror}
                </Box>
              )}

              <Button
                variant="contained"
                color="primary"
                onClick={handleSave}
                disabled={ruleloading || !isValid}
              >
                {ruleloading ? <CircularProgress size={20} /> : "Save RuleSet"}
              </Button>
            </Box>
          </Paper>
        </Box>
      )}
    </>
  );
};

export default NormalRuleFrame;
